import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  LinearProgress,
  Stack,
  Alert,
} from '@mui/material';
import { Delete, Email } from '@mui/icons-material';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { reportsApi } from 'src/api/reportsApi';
import { authApi } from 'src/api/authApi';
import { toPascalCase, initializeCompanyEmails } from 'src/utils/reportUtils';
import ReportEmailPreview from 'src/pages/WinsDashboard/components/ReportEmailPreview';

const ReportGenerationModal = ({ open, onClose, companies, dateRange }) => {
  const [companyEmails, setCompanyEmails] = useState({});
  const [companySubjects, setCompanySubjects] = useState({});
  const [companyAdditionalInfo, setCompanyAdditionalInfo] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, currentCompany: '' });
  const [generationResults, setGenerationResults] = useState(null);
  const [addingEmailFor, setAddingEmailFor] = useState(null);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Get the single company (this modal is for single company use)
  const company = companies[0];

  // Simple email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Initialize company emails and subjects when modal opens
  useEffect(() => {
    if (open && companies.length > 0) {
      const emails = initializeCompanyEmails(companies);

      // Append SEOPlus team emails to every company
      const seoplusTeamEmails = [
        { email: 'catherine@seoplus.ca', name: 'Catherine Hansen', isCustom: false },
        { email: 'annabelle.caron@seoplus.com', name: 'Annabelle Caron', isCustom: false },
        { email: 'abigail.lyons@seoplus.com', name: 'Abigail Lyon', isCustom: false },
        { email: 'rukayat.sikiru@seoplus.com', name: 'Rukayat Sikiru', isCustom: false },
      ];

      // Add SEOPlus team to each company's email list
      Object.keys(emails).forEach((companyName) => {
        emails[companyName] = [...emails[companyName], ...seoplusTeamEmails];
      });

      setCompanyEmails(emails);

      // Initialize subjects with template
      const subjects = {};
      const month = dateRange.start.format('MMMM');
      companies.forEach((c) => {
        subjects[c.name] = `PR Update (${month}) - ${c.name}`;
      });
      setCompanySubjects(subjects);

      // Initialize additional info
      const additionalInfo = {};
      companies.forEach((c) => {
        additionalInfo[c.name] = '';
      });
      setCompanyAdditionalInfo(additionalInfo);

      setGenerationResults(null);
      setAddingEmailFor(null);
      setNewEmail('');
      setNewName('');
      setEmailError('');
    }
  }, [open, companies, dateRange]);

  const handleRemoveEmail = (companyName, emailToRemove) => {
    setCompanyEmails((prev) => ({
      ...prev,
      [companyName]: (prev[companyName] || []).filter((emailObj) => emailObj.email !== emailToRemove),
    }));
  };

  const handleNameChange = (companyName, email, updatedName) => {
    setCompanyEmails((prev) => ({
      ...prev,
      [companyName]: (prev[companyName] || []).map((emailObj) => (emailObj.email === email ? { ...emailObj, name: updatedName } : emailObj)),
    }));
  };

  const handleAddEmail = (companyName) => {
    if (newEmail && newName) {
      if (!validateEmail(newEmail)) {
        setEmailError('Please enter a valid email address');
        return;
      }

      setCompanyEmails((prev) => ({
        ...prev,
        [companyName]: [...(prev[companyName] || []), { email: newEmail, name: newName, isCustom: true }],
      }));
      setAddingEmailFor(null);
      setNewEmail('');
      setNewName('');
      setEmailError('');
    }
  };

  const handleSubjectChange = (companyName, newSubject) => {
    setCompanySubjects((prev) => ({
      ...prev,
      [companyName]: newSubject,
    }));
  };

  const handleAdditionalInfoChange = (companyName, info) => {
    setCompanyAdditionalInfo((prev) => ({
      ...prev,
      [companyName]: info,
    }));
  };

  const handleGenerateReports = async () => {
    if (!company) {
      toast.error('No company available');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress({ current: 0, total: 1, currentCompany: company.name });

    const results = {
      successful: [],
      failed: [],
      totalEmails: 0,
      sentEmails: 0,
    };

    const startDate = dateRange.start.format('YYYY-MM-DD');
    const endDate = dateRange.end.format('YYYY-MM-DD');

    const companyName = company.name;
    const pascalCaseCompany = toPascalCase(companyName);
    const emails = companyEmails[companyName] || [];

    setGenerationProgress({
      current: 1,
      total: 1,
      currentCompany: companyName,
    });

    results.totalEmails += emails.length;

    try {
      // Step 1: Check if report exists
      let reportId = null;

      try {
        const existingReports = await reportsApi.fetchByCompanyName(pascalCaseCompany);

        const existingReport = existingReports?.find(
          (report) => moment(report.startDate).format('YYYY-MM-DD') === startDate && moment(report.endDate).format('YYYY-MM-DD') === endDate
        );

        if (existingReport) {
          reportId = existingReport.id;
        }
      } catch (error) {
        // No existing reports found
      }

      // Step 2: Create report if it doesn't exist
      if (!reportId) {
        try {
          const createResponse = await reportsApi.create(pascalCaseCompany, startDate, endDate);

          reportId = createResponse;
        } catch (error) {
          results.failed.push({
            company: companyName,
            error: 'Failed to create report',
            emails: emails.length,
          });
        }
      }

      // Step 3: Send emails one by one
      if (reportId) {
        let emailsSent = 0;
        const subject = companySubjects[companyName];
        const rawAdditionalInfo = companyAdditionalInfo[companyName];
        const additionalInfo = rawAdditionalInfo
          ? rawAdditionalInfo.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
          : rawAdditionalInfo;
        for (const emailObj of emails) {
          try {
            // Always send with name if available
            await authApi.expertSendToken(
              emailObj.email,
              undefined,
              reportId.toString(),
              emailObj.name || undefined,
              undefined,
              subject,
              additionalInfo
            );
            emailsSent++;
            results.sentEmails++;
          } catch (error) {
            // Continue with other emails even if one fails
          }
        }

        results.successful.push({
          company: companyName,
          reportId,
          totalEmails: emails.length,
          sentEmails: emailsSent,
        });
      }
    } catch (error) {
      results.failed.push({
        company: companyName,
        error: error.message || 'Unknown error',
        emails: emails.length,
      });
    }

    setGenerationResults(results);
    setIsGenerating(false);

    // Show summary toast
    if (results.successful.length > 0) {
      toast.success(`Reports generated for ${results.successful.length} companies. ${results.sentEmails}/${results.totalEmails} emails sent.`);
    }
    if (results.failed.length > 0) {
      toast.error(`Failed to generate reports for ${results.failed.length} companies.`);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      onClose();
      setCompanyEmails({});
      setCompanySubjects({});
      setCompanyAdditionalInfo({});
      setGenerationResults(null);
      setAddingEmailFor(null);
      setNewEmail('');
      setNewName('');
      setEmailError('');
    }
  };

  // Early return if no company
  if (!company) {
    return null;
  }

  const emails = companyEmails[company.name] || [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Send reports</Typography>
          <Typography variant="body2" color="text.secondary">
            {dateRange.start.format('MMM DD, YYYY')} - {dateRange.end.format('MMM DD, YYYY')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: { xs: 1, md: 3 }, py: 2 }}>
        {!isGenerating && !generationResults && (
          <Box sx={{ flex: 1, overflow: 'auto', pr: 1, minHeight: 0 }}>
            {/* Company Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {company.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <span>
                  {company.experts.length} expert{company.experts.length !== 1 ? 's' : ''}
                </span>
                <span>•</span>
                <span>
                  {emails.length} email{emails.length !== 1 ? 's' : ''}
                </span>
              </Typography>
            </Box>

            {/* Subject Field */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Subject:
            </Typography>
            <TextField
              fullWidth
              value={companySubjects[company.name] || ''}
              onChange={(e) => handleSubjectChange(company.name, e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              placeholder="Email subject"
            />

            {/* Experts List */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Experts:
            </Typography>
            <Box sx={{ mb: 2 }}>
              {company.experts.map((expert) => (
                <Chip key={expert.id} label={expert.name} size="small" sx={{ mr: 1, mb: 1 }} color="primary" />
              ))}
            </Box>

            {/* Email Management */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Email Recipients:
            </Typography>

            <Stack spacing={1} sx={{ mb: 3 }}>
              {emails.map((emailObj) => (
                <Box
                  key={emailObj.email}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: emailObj.isCustom ? '1px solid' : 'none',
                    borderColor: emailObj.isCustom ? 'primary.main' : 'transparent',
                  }}
                >
                  <Email fontSize="small" color="action" sx={{ mt: 1 }} />
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {emailObj.email}
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Recipient name"
                      value={emailObj.name || ''}
                      onChange={(e) => handleNameChange(company.name, emailObj.email, e.target.value)}
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          bgcolor: 'background.paper',
                        },
                      }}
                    />
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveEmail(company.name, emailObj.email)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {/* Add Email Form */}
              {addingEmailFor === company.name ? (
                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', p: 1, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <TextField fullWidth size="small" placeholder="Name (required)" value={newName} onChange={(e) => setNewName(e.target.value)} />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Email address"
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      // Clear error when user starts typing a valid email
                      if (emailError && validateEmail(e.target.value)) {
                        setEmailError('');
                      }
                    }}
                    type="email"
                    error={!!emailError}
                    helperText={emailError}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setAddingEmailFor(null);
                        setNewEmail('');
                        setNewName('');
                        setEmailError('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleAddEmail(company.name)}
                      disabled={!newEmail || !newName || !!emailError}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button size="small" variant="outlined" onClick={() => setAddingEmailFor(company.name)} sx={{ alignSelf: 'flex-start' }}>
                  Add Email
                </Button>
              )}

              {emails.length === 0 && addingEmailFor !== company.name && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No email addresses added
                </Typography>
              )}
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Email preview:
            </Typography>

            <ReportEmailPreview
              month={dateRange.start.format('MMMM')}
              year={dateRange.start.format('YYYY')}
              additionalInformation={companyAdditionalInfo[company.name] || ''}
              onAdditionalInfoChange={(info) => handleAdditionalInfoChange(company.name, info)}
            />
          </Box>
        )}

        {/* Generation Progress */}
        {isGenerating && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Generating Reports...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Processing {generationProgress.currentCompany}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(generationProgress.current / generationProgress.total) * 100}
              sx={{ width: '100%', mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {generationProgress.current} of {generationProgress.total} companies
            </Typography>
          </Box>
        )}

        {/* Generation Results */}
        {generationResults && (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Report Generation Complete
            </Typography>

            {generationResults.successful.length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Successfully generated reports for {generationResults.successful.length} companies</Typography>
                <Typography variant="body2">
                  Emails sent: {generationResults.sentEmails} of {generationResults.totalEmails}
                </Typography>
              </Alert>
            )}

            {generationResults.failed.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Failed to generate reports for {generationResults.failed.length} companies</Typography>
              </Alert>
            )}

            {/* Detailed Results */}
            <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
              {generationResults.successful.map((result) => (
                <Box key={result.company} sx={{ mb: 1, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    ✅ {result.company}
                  </Typography>
                  <Typography variant="body2">
                    Report ID: {result.reportId} • Emails: {result.sentEmails}/{result.totalEmails}
                  </Typography>
                </Box>
              ))}

              {generationResults.failed.map((result) => (
                <Box key={result.company} sx={{ mb: 1, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    ❌ {result.company}
                  </Typography>
                  <Typography variant="body2">Error: {result.error}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!isGenerating && !generationResults && (
          <>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleGenerateReports} variant="contained" startIcon={<Email />}>
              Send report
            </Button>
          </>
        )}

        {generationResults && (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ReportGenerationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      experts: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          name: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  dateRange: PropTypes.shape({
    start: PropTypes.object.isRequired, // moment object
    end: PropTypes.object.isRequired, // moment object
  }).isRequired,
};

export default ReportGenerationModal;
